import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ["chevron", "content", "panel", "text", "teaser", "trigger"]
  static values = {
    rotation: { type: Number, default: 90 },
    chevronClass: { type: String, default: "cesp-expandable-chevron" },
    contentTarget: { type: String, default: "content" },
    initialExpanded: { type: Boolean, default: false },
    dynamicText: { type: Boolean, default: false },
    collapsedText: { type: String, default: "" }
  }
  static classes = ["expanded"]

    connect() {
    // Store original text for dynamic text functionality
    if (this.dynamicTextValue && this.hasTextTarget) {
      this.originalText = this.textTarget.textContent.trim()
    }

    // Initialize expanded state
    this.expanded = this.initialExpandedValue

    // Apply initial state if starting expanded
    if (this.expanded) {
      this.applyExpandedState()
    }
  }

  // Dynamic target getter for configurable content target
  get activeContentTarget() {
    const targetName = this.contentTargetValue + "Target"
    return this[targetName]
  }

  // Check if active content target exists
  get hasActiveContentTarget() {
    const targetName = "has" + this.contentTargetValue.charAt(0).toUpperCase() +
                       this.contentTargetValue.slice(1) + "Target"
    return this[targetName]
  }

  toggle() {
    this.expanded = !this.expanded

    if (this.expanded) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  expand() {
    this.expanded = true
    this.applyExpandedState()
  }

  collapse() {
    this.expanded = false
    this.applyCollapsedState()
  }

  // Optional: when the toggling control is marked as a trigger target, keep its
  // aria-expanded in step with the disclosure. A control that never announces its
  // state fails WCAG 4.1.2, and a hard-coded aria-expanded is worse than none.
  // No-op wherever the target is absent, so existing usages are unaffected.
  syncTriggerExpanded() {
    if (!this.hasTriggerTarget) {
      return
    }

    this.triggerTargets.forEach(trigger => {
      trigger.setAttribute('aria-expanded', String(this.expanded))
    })
  }

  applyExpandedState() {
    this.syncTriggerExpanded()
    // Add rotation using custom CSS property
    this.chevronTarget.style.setProperty('--rotation', `${this.rotationValue}deg`)
    this.chevronTarget.classList.add(this.expandedClass)
    // Also add expanded class to the controller element (card) for styling
    this.element.classList.add(this.expandedClass)

    // Show content if target exists
    if (this.hasActiveContentTarget) {
      this.activeContentTarget.classList.remove('hidden')
    }

    // Hide teaser targets when expanded (e.g., truncated preview text or abstract)
    // This prevents showing both the teaser and full content simultaneously.
    // The teaser target is optional - if not present, this is a no-op.
    if (this.hasTeaserTarget) {
      this.teaserTargets.forEach(teaser => teaser.classList.add('hidden'))
    }

    // Update text to collapsed text (text to show when expanded)
    if (this.dynamicTextValue && this.hasTextTarget && this.collapsedTextValue) {
      this.textTarget.textContent = this.collapsedTextValue
    }
  }

  applyCollapsedState() {
    this.syncTriggerExpanded()
    // Remove rotation
    this.chevronTarget.style.removeProperty('--rotation')
    this.chevronTarget.classList.remove(this.expandedClass)
    // Also remove expanded class from the controller element (card)
    this.element.classList.remove(this.expandedClass)

    // Hide content if target exists
    if (this.hasActiveContentTarget) {
      this.activeContentTarget.classList.add('hidden')
    }

    // Show teaser targets when collapsed (e.g., truncated preview text or abstract)
    // This displays the preview/abstract when the full content is hidden.
    // The teaser target is optional - if not present, this is a no-op.
    if (this.hasTeaserTarget) {
      this.teaserTargets.forEach(teaser => teaser.classList.remove('hidden'))
    }

    // Restore original text
    if (this.dynamicTextValue && this.hasTextTarget && this.originalText) {
      this.textTarget.textContent = this.originalText
    }
  }
}